import React, { useState } from 'react';
import { Box, Flex, Icon, Stack, Image } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';

export interface ImageCarouselProps {
  images: string[];
  dotSize?: number;
  imageWidth?: string;
  imageHeight?: string;
}

export default function ImageCarousel({
  images,
  dotSize = 6,
  imageWidth = '80px',
  imageHeight = '80px',
}: ImageCarouselProps) {
  const dotWindowSize = 3;
  const [current, setCurrent] = useState(0);
  const [dotStart, setDotStart] = useState(0);

  function prev() {
    setCurrent(ci => {
      const prevIndex = (ci - 1 + images.length) % images.length;
      setDotStart(ds => {
        if (ci === 0) {
          return Math.max(images.length - dotWindowSize, 0);
        }
        if (prevIndex < ds) return ds - 1;
        return ds;
      });
      return prevIndex;
    });
  }

  function next() {
    setCurrent(ci => {
      const nextIndex = (ci + 1) % images.length;
      setDotStart(ds => {
        if (ci === images.length - 1) return 0;
        if (nextIndex >= ds + dotWindowSize) return ds + 1;
        return ds;
      });
      return nextIndex;
    });
  }

  return (
    <Stack textAlign="center" align="center" spacing={1}>
      <Box onClick={next} cursor="pointer">
        <Image
          src={images[current]}
          alt={`carousel-${current}`}
          boxSize={imageWidth}
          maxW={imageWidth}
          h={imageHeight}
          objectFit="contain"
        />
      </Box>

      {images.length > 1 && (
        <Flex align="center" userSelect="none">
          <Icon
            as={ChevronLeftIcon}
            boxSize={4}
            onClick={prev}
            _hover={{ cursor: 'pointer', color: 'gray.500' }}
            mr={2}
          />

          {images.slice(dotStart, dotStart + dotWindowSize).map((_, i) => {
            const idx = dotStart + i;
            return (
              <Box
                key={idx}
                w={`${dotSize}px`}
                h={`${dotSize}px`}
                mx={1}
                borderRadius="full"
                bg={idx === current ? 'gray.700' : 'gray.300'}
              />
            );
          })}

          <Icon
            as={ChevronRightIcon}
            boxSize={4}
            onClick={next}
            _hover={{ cursor: 'pointer', color: 'gray.500' }}
            ml={2}
          />
        </Flex>
      )}
    </Stack>
  );
}